import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from "react-i18next";
import {IonCol, IonIcon, IonItem, IonRow} from "@ionic/react";
import {checkmarkCircle} from "ionicons/icons";
import Modal from "../../../components/modal/modal.component";
import {StyledButton, StyledRedButton, StyledWiteButton} from "@components/button/button.style";
import {StyledDataTable} from "@components/content/content.style";
import {StyledIonLabel} from "@components/form/input/label.style";
import {StyledIonCheckbox} from "@components/form/input/checkbox.style";
import moment from "moment";
import {Proposal} from "@models/calendar";
import {acceptProposal, rejectProposal} from "@services/calendar/proposal.service";
import {useToast} from "@context/toast.context";
import ProposalRejectModal from "@app/availabilityPage/modals/proposalRejectModal.component";
import {StyledAcceptedBanner, StyledEventPreviewFooter, StyledEventPreviewLayout, StyledEventPreviewScroll} from "@app/availabilityPage/modals/eventPreviewModal.style";

type EventPreviewModalProps = {
    proposal: Proposal;
    isOpen: boolean;
    onDone: () => void,
    onClose: () => void;
};

const REJECTION_BUFFER_HOURS = 1;

const getErrorCode = (response: any, error?: any): string | null => {
    if (response?.data?.result === 'error') {
        return response.data.message ?? 'UNKNOWN';
    }

    if (error) {
        return error?.response?.data?.message ?? 'UNKNOWN';
    }

    return null;
};

const getErrorTranslationKey = (code: string | null, fallback: string): string => {
    switch (code) {
        case 'PROPOSAL_HAS_ALREADY_STARTED':
            return 'proposal.error.hasAlreadyStarted';
        case 'PROPOSAL_REJECTION_BUFFER_EXCEEDED':
            return 'proposal.error.rejectionBufferExceeded';
        case 'PROPOSAL_REJECTION_DISABLED':
            return 'proposal.error.rejectionDisabled';
        case 'NOT_AVAILABLE':
            return 'proposal.error.notAvailable';
        case 'PLACE_NOT_PREFERRED':
            return 'proposal.error.placeNotPreferred';
        default:
            return fallback;
    }
};

const EventPreviewModal: React.FC<EventPreviewModalProps> = ({isOpen, proposal, onClose, onDone}: EventPreviewModalProps) => {
    const {t} = useTranslation();
    const {showToast} = useToast();

    const [isRejectModalOpen, setIsRejectModalOpen] = useState<boolean>(false);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [adjustAvailability, setAdjustAvailability] = useState<boolean>(false);
    const [confirmPlaceAddition, setConfirmPlaceAddition] = useState<boolean>(false);

    const submittingRef = useRef<boolean>(false);

    useEffect(() => {
        if (isOpen) {
            setAdjustAvailability(false);
            setConfirmPlaceAddition(false);
        }
    }, [isOpen, proposal.id]);

    const status = proposal.attributes.status;
    const canAccept = status === 'PROPOSED';
    const withinRejectionBuffer = moment(proposal.attributes.originalStartTime, 'YYYY-MM-DD HH:mm').isSameOrBefore(moment().add(REJECTION_BUFFER_HOURS, 'hours'));
    const acceptanceEnabled = proposal.attributes.acceptanceEnabled !== false;
    const canReject = acceptanceEnabled && (status === 'PROPOSED' || (status === 'ACCEPTED' && !withinRejectionBuffer));
    const isAccepted = status === 'ACCEPTED';
    const isRejected = status === 'REJECTED' || status === 'SUGGESTED_REPLACEMENT';

    const hasAvailabilityMismatch = proposal.attributes.hasAvailabilityMismatch === true;
    const hasPlaceMismatch = proposal.attributes.hasPlaceMismatch === true;

    const showAdjustAvailability = hasAvailabilityMismatch && (canAccept || canReject);
    const showConfirmPlaceAddition = hasPlaceMismatch && (canAccept || canReject);

    const showError = (key: string) => {
        showToast({
            message: t(key),
            type: 'danger',
            duration: 3000,
        });
    };

    const handleProposalAccept = async () => {
        if (submittingRef.current) {
            return;
        }

        if (showAdjustAvailability && !adjustAvailability) {
            showError('proposal.error.adjustAvailabilityRequired');

            return;
        }

        if (showConfirmPlaceAddition && !confirmPlaceAddition) {
            showError('proposal.error.confirmPlaceAdditionRequired');

            return;
        }

        submittingRef.current = true;
        setSubmitting(true);

        try {
            const response = await acceptProposal(proposal.id, confirmPlaceAddition, adjustAvailability);
            const errorCode = getErrorCode(response);

            if (errorCode) {
                showError(getErrorTranslationKey(errorCode, 'proposal.error.accept'));

                return;
            }

            onClose();
            onDone();
        } catch (error) {
            showError(getErrorTranslationKey(getErrorCode(null, error), 'proposal.error.accept'));

            console.error(error);
        } finally {
            submittingRef.current = false;
            setSubmitting(false);
        }
    }

    const handleProposalReject = async (reason: string, replacement: string | null) => {
        if (submittingRef.current) {
            return;
        }

        submittingRef.current = true;
        setSubmitting(true);

        try {
            const response = await rejectProposal(proposal.id, reason, replacement);
            const errorCode = getErrorCode(response);

            if (errorCode) {
                showError(getErrorTranslationKey(errorCode, 'proposal.error.reject'));

                return;
            }

            setIsRejectModalOpen(false);
            onClose();
            onDone();
        } catch (error) {
            showError(getErrorTranslationKey(getErrorCode(null, error), 'proposal.error.reject'));

            console.error(error);
        } finally {
            submittingRef.current = false;
            setSubmitting(false);
        }
    }

    const placeName = proposal.attributes.placeOfService;
    const placeAddress = proposal.attributes.placeOfServiceAddress;

    return (
        <>
            <ProposalRejectModal
                isOpen={isRejectModalOpen}
                submitting={submitting}
                onClose={() => setIsRejectModalOpen(false)}
                onConfirm={(reason, replacement) => handleProposalReject(reason, replacement)}
            />
            <Modal initialBreakpoint={0.99} breakpoints={[0, 0.99]} isOpen={isOpen} onDidDismiss={() => onClose()}>
                <StyledEventPreviewLayout>
                <h6 className="description">{t('notificationTab.timetablePane.preview.title', {coordinator: proposal.attributes.coordinator})}</h6>
                <StyledEventPreviewScroll>
                <StyledDataTable>
                    <div className="data-row">
                        <div className="data-title">{t('notificationTab.timetablePane.preview.place')}</div>
                        <div className="data-value">
                            {
                                placeAddress
                                    ? <div className="data-icon-title titles" style={{padding: 0}}>
                                        <div className="main-title">{placeAddress}</div>
                                        <div className="sub-title">{placeName}</div>
                                    </div>
                                    : (placeName || '--')
                            }
                        </div>
                        <div className="data-action"></div>
                    </div>
                    <div className="data-row">
                        <div className="data-title">{t('notificationTab.timetablePane.preview.jobPosition')}</div>
                        <div className="data-value long-text">{proposal.attributes.jobPosition || '--'}</div>
                        <div className="data-action"></div>
                    </div>
                    <div className="data-row">
                        <div className="data-title">{t('notificationTab.timetablePane.preview.from')}</div>
                        <div
                            className="data-value">{moment(proposal.attributes.originalStartTime, 'YYYY-MM-DD HH:mm').format('YYYY-MM-DD HH:mm')}</div>
                        <div className="data-action"></div>
                    </div>
                    <div className="data-row">
                        <div className="data-title">{t('notificationTab.timetablePane.preview.to')}</div>
                        <div
                            className="data-value">{moment(proposal.attributes.originalEndTime, 'YYYY-MM-DD HH:mm').format('YYYY-MM-DD HH:mm')}</div>
                        <div className="data-action"></div>
                    </div>
                    <div className="data-row">
                        <div className="data-title">{t('notificationTab.timetablePane.preview.details')}</div>
                        <div className="data-value">{proposal.attributes.description || '--'}</div>
                        <div className="data-action"></div>
                    </div>
                    {
                        showAdjustAvailability &&
                        <IonRow style={{width: "100%", marginTop: "10px"}}>
                            <IonItem lines="none" style={{'--padding-start': '10px', '--inner-padding-start': '0', '--inner-padding-end': '0', 'width': '100%'}}>
                                <StyledIonLabel className="ion-text-wrap required">{t('notificationTab.taskItem.adjustAvailability')}</StyledIonLabel>
                                <StyledIonCheckbox
                                    slot="start"
                                    disabled={submitting}
                                    onIonChange={(e) => setAdjustAvailability(e.detail.checked)}
                                    checked={adjustAvailability}
                                    value={adjustAvailability}
                                />
                            </IonItem>
                        </IonRow>
                    }
                    {
                        showConfirmPlaceAddition &&
                        <IonRow style={{width: "100%", marginTop: "10px"}}>
                            <IonItem lines="none" style={{'--padding-start': '10px', '--inner-padding-start': '0', '--inner-padding-end': '0', 'width': '100%'}}>
                                <StyledIonLabel className="ion-text-wrap required">{t('notificationTab.taskItem.confirmPlaceAddition')}</StyledIonLabel>
                                <StyledIonCheckbox
                                    slot="start"
                                    disabled={submitting}
                                    onIonChange={(e) => setConfirmPlaceAddition(e.detail.checked)}
                                    checked={confirmPlaceAddition}
                                    value={confirmPlaceAddition}
                                />
                            </IonItem>
                        </IonRow>
                    }
                </StyledDataTable>
                </StyledEventPreviewScroll>

                <StyledEventPreviewFooter>
                {
                    isAccepted &&
                    <IonRow>
                        <IonCol size="12">
                            <StyledAcceptedBanner>
                                <IonIcon icon={checkmarkCircle}/>
                                {t('notificationTab.taskItem.accepted')}
                            </StyledAcceptedBanner>
                        </IonCol>
                    </IonRow>
                }
                {
                    canAccept &&
                    <IonRow>
                        <IonCol size="6">
                            <StyledButton
                                className="accept"
                                disabled={submitting}
                                onClick={() => handleProposalAccept()}>
                                {t('notificationTab.taskItem.accept')}
                            </StyledButton>
                        </IonCol>
                        <IonCol size="6">
                            <StyledWiteButton
                                disabled={submitting}
                                onClick={() => setIsRejectModalOpen(true)}>
                                {t('notificationTab.taskItem.dismiss')}
                            </StyledWiteButton>
                        </IonCol>
                    </IonRow>
                }
                {
                    !canAccept && canReject &&
                    <IonRow>
                        <IonCol size="12">
                            <StyledRedButton
                                disabled={submitting}
                                onClick={() => setIsRejectModalOpen(true)}>
                                {t('notificationTab.taskItem.dismiss')}
                            </StyledRedButton>
                        </IonCol>
                    </IonRow>
                }
                {
                    isRejected &&
                    <h6 className="description" style={{textAlign: 'center', width: '100%'}}>{t('notificationTab.taskItem.dismissed')}</h6>
                }
                </StyledEventPreviewFooter>
                </StyledEventPreviewLayout>
            </Modal>
        </>
    );
};

export default EventPreviewModal;
